import {
  LineChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
} from 'recharts';
import { Link } from 'react-router';
import { EmptyChart } from './ui/EmptyChart.js';
import type { ReliabilityTier, VisibilityScore } from '../types/index.js';
import { reliabilityTierForSampleSize } from './EngineVisibilityCard.js';
import {
  ENGINE_LABELS,
  CHART_INK,
  CHART_ACTION,
  CHART_INK_MUTED,
  HATCH_FILL,
  ChartHatchDefs,
  AXIS_STROKE,
  GRID_STROKE,
  GRID_DASH,
  AXIS_TICK,
  CHART_MARGIN,
  TOOLTIP_CONTAINER,
  TOOLTIP_LABEL,
  TOOLTIP_ROW,
  TOOLTIP_MUTED,
} from './chartTheme.js';

interface VisibilityChartProps {
  history: VisibilityScore[];
  engines: Set<string>;
}

/** Multi-brand chart mode props (Spec 062) */
export interface BrandSeries {
  brand_id: string;
  brand_name: string;
  is_selected: boolean;
  history: VisibilityScore[];
}

export interface MultiBrandChartProps {
  series: BrandSeries[];
}

interface ChartPoint {
  date: string;
  [key: string]: number | string | null | undefined;
}

// Spec 181: human reliability labels — same wording as ConfidenceBadge.
const RELIABILITY_LABELS: Record<ReliabilityTier, string> = {
  directional: 'Directional',
  operational: 'Operational',
  high_confidence: 'High confidence',
};

function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Aggregate history rows by date + engine across prompt categories.
 * Computes weighted average mention_rate by sample_size.
 */
const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));

function aggregateByDateEngine(history: VisibilityScore[]): ChartPoint[] {
  const map = new Map<string, Map<string, { totalMentioned: number; totalSamples: number; ciLower: number; ciUpper: number; unpromptedMentioned: number; unpromptedSamples: number }>>();

  for (const row of history) {
    if (!map.has(row.period)) map.set(row.period, new Map());
    const dateMap = map.get(row.period)!;
    if (!dateMap.has(row.engine)) {
      dateMap.set(row.engine, { totalMentioned: 0, totalSamples: 0, ciLower: 0, ciUpper: 0, unpromptedMentioned: 0, unpromptedSamples: 0 });
    }
    const agg = dateMap.get(row.engine)!;
    const mr = row.mention_rate ?? 0;
    const ss = row.sample_size;
    agg.totalMentioned += Math.round(mr * ss);
    agg.totalSamples += ss;

    // Spec 180: accumulate unprompted basis (mirrors the per-engine card's
    // `unprompted_mention_rate != null ? unprompted : raw`). Plotted below
    // with a per-point raw fallback when a point has no unprompted samples.
    agg.unpromptedMentioned += row.unprompted_mentioned_count ?? 0;
    agg.unpromptedSamples += row.unprompted_sample_size ?? 0;

    // Track raw CI weighted by sample size (fallback when no unprompted samples)
    const ci = row.confidence_interval?.mention_rate;
    if (ci) {
      agg.ciLower += ci.lower * ss;
      agg.ciUpper += ci.upper * ss;
    } else {
      agg.ciLower += mr * ss;
      agg.ciUpper += mr * ss;
    }
  }

  const dates = Array.from(map.keys()).sort();
  return dates.map((date) => {
    const point: ChartPoint = { date: formatDate(date) };
    const dateMap = map.get(date)!;
    for (const [engine, agg] of dateMap) {
      // Spec 180: plot the unprompted rate; per-point fallback to raw.
      const rate =
        agg.unpromptedSamples > 0
          ? agg.unpromptedMentioned / agg.unpromptedSamples
          : agg.totalSamples > 0
            ? agg.totalMentioned / agg.totalSamples
            : null;
      point[engine] = rate != null ? Math.round(rate * 1000) / 10 : null;
      // Report the n that backs the plotted rate.
      const plottedN = agg.unpromptedSamples > 0 ? agg.unpromptedSamples : agg.totalSamples;
      point[`${engine}_samples`] = plottedN;
      // Spec 181: carry the per-point reliability tier (reuse 179's helper) so
      // the chart can de-emphasize thin-sample points. Only when the point has a
      // value (n>0) — leave absent for null rates so gaps stay unlabeled.
      if (rate != null) {
        point[`${engine}_reliability`] = reliabilityTierForSampleSize(plottedN);
      }

      if (agg.unpromptedSamples > 0) {
        // 95% Wald interval from the unprompted (p, n).
        const p = agg.unpromptedMentioned / agg.unpromptedSamples;
        const half = 1.96 * Math.sqrt((p * (1 - p)) / agg.unpromptedSamples);
        point[`${engine}_ci_lower`] = Math.round(clamp01(p - half) * 1000) / 10;
        point[`${engine}_ci_upper`] = Math.round(clamp01(p + half) * 1000) / 10;
      } else if (agg.totalSamples > 0) {
        // Raw fallback: existing sample-weighted CI logic.
        point[`${engine}_ci_lower`] = Math.round((agg.ciLower / agg.totalSamples) * 1000) / 10;
        point[`${engine}_ci_upper`] = Math.round((agg.ciUpper / agg.totalSamples) * 1000) / 10;
      }
    }
    return point;
  });
}

/** Aggregate multi-brand data: one line per brand, averaged across all engines */
function aggregateByDateBrand(series: BrandSeries[]): ChartPoint[] {
  const dateMap = new Map<string, Map<string, { totalMentioned: number; totalSamples: number; unpromptedMentioned: number; unpromptedSamples: number }>>();

  for (const brand of series) {
    const key = brand.brand_name;
    for (const row of brand.history) {
      if (!dateMap.has(row.period)) dateMap.set(row.period, new Map());
      const dm = dateMap.get(row.period)!;
      if (!dm.has(key)) dm.set(key, { totalMentioned: 0, totalSamples: 0, unpromptedMentioned: 0, unpromptedSamples: 0 });
      const agg = dm.get(key)!;
      const mr = row.mention_rate ?? 0;
      const ss = row.sample_size;
      agg.totalMentioned += Math.round(mr * ss);
      agg.totalSamples += ss;
      // Spec 180: keep the basis consistent with the engine view.
      agg.unpromptedMentioned += row.unprompted_mentioned_count ?? 0;
      agg.unpromptedSamples += row.unprompted_sample_size ?? 0;
    }
  }

  const dates = Array.from(dateMap.keys()).sort();
  return dates.map((date) => {
    const point: ChartPoint = { date: formatDate(date) };
    const dm = dateMap.get(date)!;
    for (const [brandName, agg] of dm) {
      // Spec 180: plot the unprompted rate; per-point fallback to raw.
      const rate =
        agg.unpromptedSamples > 0
          ? agg.unpromptedMentioned / agg.unpromptedSamples
          : agg.totalSamples > 0
            ? agg.totalMentioned / agg.totalSamples
            : null;
      point[brandName] = rate != null ? Math.round(rate * 1000) / 10 : null;
      // Spec 181: per-point reliability for the brand-mode lines (same basis).
      if (rate != null) {
        const plottedN = agg.unpromptedSamples > 0 ? agg.unpromptedSamples : agg.totalSamples;
        point[`${brandName}_reliability`] = reliabilityTierForSampleSize(plottedN);
      }
    }
    return point;
  });
}

/**
 * Spec 181: reliability-conditional dot factory. Reads the per-point reliability
 * tier (set by the aggregators above) keyed off the line's dataKey and styles the
 * dot so low-confidence (directional) points read as faint/hollow while
 * well-sampled points render solid. Returns null for null/absent points so
 * gaps stay gaps (connectNulls still bridges them as a thin line).
 *
 * Spec 234: always called with the ink colour now — reliability is encoded
 * by dot weight/hollowness, never by a second hue.
 */
interface ReliabilityDotProps {
  cx?: number;
  cy?: number;
  dataKey?: string;
  payload?: ChartPoint;
  value?: number | null;
}

const makeReliabilityDot = (color: string) => (props: ReliabilityDotProps) => {
  const { cx, cy, dataKey, payload, value } = props;
  if (value == null || cx == null || cy == null) return null;
  const tier = payload?.[`${dataKey}_reliability`] as ReliabilityTier | undefined;
  const key = `${dataKey}-${cx}-${cy}`;
  if (tier === 'directional') {
    // Small hollow faint dot — low confidence.
    return <circle key={key} cx={cx} cy={cy} r={2.5} fill="var(--hub-color-surface)" stroke={color} strokeWidth={1} opacity={0.7} />;
  }
  if (tier === 'operational') {
    return <circle key={key} cx={cx} cy={cy} r={3} fill="var(--hub-color-surface)" stroke={color} strokeWidth={2} />;
  }
  // high_confidence or missing reliability → solid filled dot.
  return <circle key={key} cx={cx} cy={cy} r={3.5} fill={color} stroke={color} />;
};

/** Single-series tooltip for one engine's small-multiple plot. */
function EngineTooltipContent({ active, payload, label, engine }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number; payload: ChartPoint }>;
  label?: string;
  engine: string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload.find((p) => p.dataKey === engine) ?? payload[0];
  if (!point) return null;
  const samples = point.payload[`${engine}_samples`];
  const ciLower = point.payload[`${engine}_ci_lower`];
  const ciUpper = point.payload[`${engine}_ci_upper`];
  const reliability = point.payload[`${engine}_reliability`] as ReliabilityTier | undefined;
  const reliabilityLabel = reliability ? RELIABILITY_LABELS[reliability] : null;

  return (
    <div className={TOOLTIP_CONTAINER}>
      <p className={TOOLTIP_LABEL}>{label}</p>
      <div className={TOOLTIP_ROW}>
        <span className="text-slate-700">{point.value != null ? `${point.value}%` : 'N/A'}</span>
        {ciLower != null && ciUpper != null && (
          <span className={TOOLTIP_MUTED}>CI: {ciLower}%-{ciUpper}%</span>
        )}
        {samples != null && (
          <span className={TOOLTIP_MUTED}>
            n={samples}
            {reliabilityLabel && ` · ${reliabilityLabel}`}
          </span>
        )}
      </div>
    </div>
  );
}

function BrandTooltipContent({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ dataKey: string; value: number }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;

  return (
    <div className={TOOLTIP_CONTAINER}>
      <p className={TOOLTIP_LABEL}>{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} className={TOOLTIP_ROW}>
          <span className="font-medium text-slate-700">{p.dataKey}:</span>
          <span className="text-slate-700">{p.value != null ? `${p.value}%` : 'N/A'}</span>
        </div>
      ))}
    </div>
  );
}

const PCT_TICK = (v: number) => `${v}%`;

/**
 * One engine's plot — spec 234 §4: the multi-engine trend is small
 * multiples, monochrome, each directly labelled with the engine name.
 * Identity comes from this HTML heading, never from a colour-keyed legend.
 */
function EngineMiniChart({ engine, data, isSinglePoint }: {
  engine: string;
  data: ChartPoint[];
  isSinglePoint: boolean;
}) {
  const label = ENGINE_LABELS[engine] ?? engine;

  return (
    <div data-testid="engine-mini-chart" data-engine={engine}>
      <div className="mb-1 text-xs font-medium text-slate-600">{label}</div>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          {isSinglePoint ? (
            <BarChart data={data} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} />
              <XAxis dataKey="date" tick={AXIS_TICK} stroke={AXIS_STROKE} />
              <YAxis domain={[0, 100]} tick={AXIS_TICK} stroke={AXIS_STROKE} tickFormatter={PCT_TICK} />
              <Tooltip content={<EngineTooltipContent engine={engine} />} />
              <Bar dataKey={engine} fill={CHART_INK} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          ) : (
            <ComposedChart data={data} margin={CHART_MARGIN}>
              <ChartHatchDefs />
              <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} />
              <XAxis dataKey="date" tick={AXIS_TICK} stroke={AXIS_STROKE} />
              <YAxis domain={[0, 100]} tick={AXIS_TICK} stroke={AXIS_STROKE} tickFormatter={PCT_TICK} />
              <Tooltip content={<EngineTooltipContent engine={engine} />} />
              {/* The CI range is a hatched band, never a tinted fill (spec 234 §5). */}
              <Area
                dataKey={`${engine}_ci_upper`}
                stroke="none"
                fill={HATCH_FILL}
                type="monotone"
                isAnimationActive={false}
                dot={false}
                activeDot={false}
                legendType="none"
              />
              <Line
                type="monotone"
                dataKey={engine}
                stroke={CHART_INK}
                strokeWidth={2}
                dot={makeReliabilityDot(CHART_INK) as any}
                activeDot={{ r: 5 }}
                connectNulls
              />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** Multi-brand visibility chart (Spec 062) */
export function MultiBrandVisibilityChart({ series }: MultiBrandChartProps) {
  if (series.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center hub-plate--flat bg-gray-50">
        <p className="text-sm font-medium text-gray-400">No competitive visibility data</p>
      </div>
    );
  }

  const data = aggregateByDateBrand(series);
  if (data.length === 0) {
    return (
      // Spec 248 (review P1-7): Kridt's Empty chart — the frame at the chart's
      // real height (h-64, 256px) instead of a grey rectangle.
      <EmptyChart
        height={256}
        title="No visibility data yet"
        text="The trend draws once the first queries have run."
        action={<Link to="/prompts" className="hub-btn hub-btn--ghost">Set up prompts</Link>}
        testId="visibility-chart-empty"
      />
    );
  }

  // Spec 234: same small-multiples treatment as the engine trend — one plot
  // per brand, directly labelled, no colour-keyed legend. Spec 256: the
  // selected brand is the one mark that carries the point, so its line is the
  // chart's one accent (Skagen) and bolder; every other brand is ink-muted, so
  // the brand reads first.
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {series.map((brand) => {
        const stroke = brand.is_selected ? CHART_ACTION : CHART_INK_MUTED;
        return (
        <div
          key={brand.brand_id}
          data-testid="brand-mini-chart"
          data-brand={brand.brand_id}
          data-series={brand.is_selected ? 'brand' : 'other'}
        >
          <div className={`mb-1 text-xs font-medium ${brand.is_selected ? 'text-slate-900' : 'text-slate-600'}`}>
            {brand.brand_name}
          </div>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray={GRID_DASH} stroke={GRID_STROKE} />
                <XAxis dataKey="date" tick={AXIS_TICK} stroke={AXIS_STROKE} />
                <YAxis domain={[0, 100]} tick={AXIS_TICK} stroke={AXIS_STROKE} tickFormatter={PCT_TICK} />
                <Tooltip content={<BrandTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey={brand.brand_name}
                  className={brand.is_selected ? 'hub-trend-line--brand' : 'hub-trend-line--other'}
                  stroke={stroke}
                  strokeWidth={brand.is_selected ? 3 : 1.5}
                  dot={makeReliabilityDot(stroke) as any}
                  activeDot={{ r: brand.is_selected ? 6 : 4 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        );
      })}
    </div>
  );
}

export function VisibilityChart({ history, engines }: VisibilityChartProps) {
  if (history.length === 0) {
    return (
      // Spec 248 (review P1-7): the frame keeps its real height; one line, one action.
      <EmptyChart
        height={256}
        title="No visibility data yet"
        text="Scores appear once queries are executed and analyzed."
        action={<Link to="/prompts" className="hub-btn hub-btn--ghost">Set up prompts</Link>}
        testId="visibility-chart-empty"
      />
    );
  }

  const data = aggregateByDateEngine(history);
  const activeEngines = Array.from(engines);
  const isSinglePoint = data.length === 1;

  // Spec 234 §4: the engine trend is small multiples — one monochrome plot
  // per engine, each directly labelled from ENGINE_LABELS. No colour-keyed
  // legend: with a direct label per plot, a legend would only repeat it.
  return (
    <div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {activeEngines.map((engine) => (
          <EngineMiniChart key={engine} engine={engine} data={data} isSinglePoint={isSinglePoint} />
        ))}
      </div>
      {isSinglePoint && (
        <p className="mt-2 text-center text-xs text-gray-400">
          Trends will appear as more data is collected over time
        </p>
      )}
    </div>
  );
}
